/**
 * Impersonation is read-only. An impersonation session (auth.sessions
 * `impersonated_by` set) may read anything the target can read and write
 * nothing: withAuth and the agents proxy refuse non-GET requests, and the
 * better-auth hook in impersonation-plugin.ts refuses the auth endpoints.
 *
 * Client-safe on purpose (no db, no server-only): the browser reads the code
 * and message to recognise a refusal, and the eligibility rules stay unit
 * testable without a database.
 */

/** `code` on every 403 an impersonation session gets for attempting a write. */
export const READ_ONLY_IMPERSONATION_CODE = 'READ_ONLY_IMPERSONATION'

export const READ_ONLY_IMPERSONATION_MESSAGE =
  "You're viewing Atlas as another user. Impersonation is read-only, so changes aren't saved."

/** Matches better-auth's admin-plugin default, which the stop path inherits. */
export const IMPERSONATION_SESSION_SECONDS = 60 * 60

export function isReadOnlyMethod(method: string): boolean {
  const upper = method.toUpperCase()
  return upper === 'GET' || upper === 'HEAD' || upper === 'OPTIONS'
}

export type ImpersonationDenial =
  | 'NOT_ALLOWED'
  | 'ALREADY_IMPERSONATING'
  | 'SELF'
  | 'TARGET_IS_ADMIN'
  | 'TARGET_IS_STAFF'

export type ImpersonationDecision =
  | { ok: true }
  | { ok: false; reason: ImpersonationDenial; message: string }

/** Everything the decision needs, resolved by the caller. */
export interface ImpersonationFacts {
  actorId: string
  targetId: string
  /** The caller's own session is already an impersonation. */
  actorIsImpersonating: boolean
  /** `users:impersonate` at the caller's active org. */
  actorCanImpersonate: boolean
  actorIsSystemAdmin: boolean
  /** system:read via any path, or better-auth `user.role` includes admin. */
  targetIsAdmin: boolean
  /** WorldVue staff: staff email, internal-org member, or internal access. */
  targetIsStaff: boolean
}

const MESSAGES: Record<ImpersonationDenial, string> = {
  NOT_ALLOWED: "You don't have permission to impersonate users.",
  ALREADY_IMPERSONATING:
    'End the current impersonation before starting another.',
  SELF: "You can't impersonate yourself.",
  TARGET_IS_ADMIN: "System administrators can't be impersonated.",
  TARGET_IS_STAFF: 'Only system administrators can impersonate WorldVue staff.'
}

function deny(reason: ImpersonationDenial): ImpersonationDecision {
  return { ok: false, reason, message: MESSAGES[reason] }
}

/**
 * Who may impersonate whom. Read-only still exposes everything the target can
 * read, so the target set is the real control: holders of `users:impersonate`
 * (CSMs, Field Ops Managers) reach external users only, and only system admins
 * reach staff. Nobody reaches a system admin, and nobody chains impersonations.
 */
export function decideImpersonation(
  facts: ImpersonationFacts
): ImpersonationDecision {
  if (facts.actorIsImpersonating) return deny('ALREADY_IMPERSONATING')
  if (!facts.actorCanImpersonate) return deny('NOT_ALLOWED')
  if (facts.actorId === facts.targetId) return deny('SELF')
  if (facts.targetIsAdmin) return deny('TARGET_IS_ADMIN')
  if (facts.targetIsStaff && !facts.actorIsSystemAdmin) {
    return deny('TARGET_IS_STAFF')
  }
  return { ok: true }
}
